import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import EmployeePicker from '../../components/EmployeePicker';
import Pagination from '../../components/Pagination';
import BulkActionBar from '../../components/BulkActionBar';
import SegmentedFilterBar from '../../components/SegmentedFilterBar';
import ScopeToggle from '../../components/ScopeToggle';
import { useScope } from '../../lib/hooks/useScope';
import { usePaginated } from '../../lib/hooks/usePaginated';
import { useRowSelection } from '../../lib/hooks/useRowSelection';
import { useSortable, SortIcon } from '../../lib/hooks/useSortable';
import { formatDate } from '../../lib/format';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { Plus, Trash2, Edit2, X, Star, Mail, Phone, Download, Send, Upload, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import CompanyPicker from '../../components/crm/CompanyPicker';
import EntityActivityDrawer from '../../components/crm/EntityActivityDrawer';
import LocationCombobox from '../../components/LocationCombobox';

// Canonical CRM Tag list (Iteration 54)
const CRM_TAGS = ['Decision Maker', 'Influencer', 'Recommander', 'Consultant', 'Architect', 'Owner/MD', 'Govt Official', 'VIP'];

const initialForm = {
  name: '', email: '', phone: '', designation: '',
  company_id: '', is_primary: false, tags: [], owner: '', linkedin: '', notes: '',
  location: '',
};

export default function Contacts() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [drawerContact, setDrawerContact] = useState(null);
  const [contactDeals, setContactDeals] = useState([]);

  const loadContactDeals = useCallback(async (contactId) => {
    try {
      const r = await apiGet(`/crm/contacts/${contactId}/deals`);
      setContactDeals(r.deals || []);
    } catch { setContactDeals([]); }
  }, []);

  useEffect(() => {
    if (drawerContact?.id) loadContactDeals(drawerContact.id);
    else setContactDeals([]);
  }, [drawerContact?.id, loadContactDeals]);
  const [form, setForm] = useState(initialForm);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const { scope, setScope } = useScope('contacts');
  const { sorted, sortKey, sortDir, getProps } = useSortable(filteredContacts, { defaultKey: 'created_at', defaultDir: 'desc' });

  const pagination = usePaginated(sorted, { defaultPageSize: 10 });
  const pageIds = pagination.paged.map((c) => c.id);
  const selection = useRowSelection();

  // Map company_id → {is_consultant, consultant_type} for inline badging
  const companyMap = useMemo(() => {
    const m = {};
    (companies || []).forEach((co) => { m[co.id] = co; });
    return m;
  }, [companies]);

  // Decorate contacts with consultant_type + computed location (city/state fallback)
  const decoratedContacts = useMemo(() => contacts.map((c) => {
    const co = companyMap[c.company_id];
    const loc = c.location || [c.city, c.state].filter(Boolean).join(', ');
    return {
      ...c,
      location: loc,
      consultant_type: co?.is_consultant ? (co.consultant_type || 'Consultant') : '',
      is_consultant_contact: !!co?.is_consultant,
    };
  }), [contacts, companyMap]);

  // Build filter definitions from the loaded data
  const filterDefs = useMemo(() => {
    const designations = Array.from(new Set(decoratedContacts.map((c) => c.designation).filter(Boolean))).sort();
    const companyNames = Array.from(new Set(decoratedContacts.map((c) => c.company_name).filter(Boolean))).sort();
    const locations = Array.from(new Set(decoratedContacts.map((c) => c.location).filter(Boolean))).sort();
    const owners = Array.from(new Set(decoratedContacts.map((c) => c.owner).filter(Boolean))).sort();
    return [
      { key: 'company_name', label: 'Company', type: 'multiSelect', options: companyNames },
      { key: 'designation', label: 'Job Title', type: 'multiSelect', options: designations },
      { key: 'location', label: 'Location', type: 'multiSelect', options: locations },
      { key: 'consultant_type', label: 'Consultant Type', type: 'multiSelect', options: ['PMC', 'Cost Consultant', 'Structural Consultant', 'Architect'] },
      { key: 'tags', label: 'Tags', type: 'tags', options: allTags.map((t) => t.name || t) },
      { key: 'owner', label: 'Owner', type: 'multiSelect', options: owners },
    ];
  }, [decoratedContacts, allTags]);

  const handleBulkDelete = async () => {
    const ids = Array.from(selection.selected);
    if (!ids.length || !window.confirm(`Delete ${ids.length} selected contact${ids.length === 1 ? '' : 's'}?\n\nThis action cannot be undone.`)) return;
    try {
      await Promise.all(ids.map((id) => apiDel(`/crm/contacts/${id}`)));
      toast.success(`Deleted ${ids.length} contact${ids.length === 1 ? '' : 's'}`);
      selection.clear();
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Bulk delete failed'); }
  };

  const handleBulkExport = () => {
    const rows = contacts.filter((c) => selection.selected.has(c.id));
    const csv = [
      ['Name', 'Email', 'Phone', 'Designation', 'Company', 'Owner', 'Tags', 'Is Primary'].join(','),
      ...rows.map((c) => [c.name, c.email, c.phone, c.designation, c.company_name, c.owner, (c.tags || []).join('; '), c.is_primary ? 'Yes' : 'No'].map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `contacts-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} contact${rows.length === 1 ? '' : 's'}`);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const load = async () => {
    if (!scope) return;
    try {
      const [cRes, coRes, tagRes] = await Promise.all([
        apiGet(`/crm/contacts?scope=${scope}`),
        apiGet('/crm/companies'),
        apiGet('/crm/tags').catch(() => ({ tags: [] })),
      ]);
      setContacts(cRes.contacts || []);
      setCompanies(coRes.companies || []);
      setAllTags(tagRes.tags || []);
    } catch (err) { console.error(err); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, company_id: form.company_id || null };
      if (editing) {
        const updated = await apiPut(`/crm/contacts/${editing.id}`, payload);
        toast.success('Updated');
        if (drawerContact?.id === editing.id) {
          setDrawerContact(prev => ({ ...prev, ...updated }));
        }
      } else {
        await apiPost('/crm/contacts', payload);
        toast.success('Created');
      }
      setShowForm(false); setEditing(null); setForm(initialForm); load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || 'Validation failed');
      } else {
        toast.error(detail || 'Failed');
      }
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this contact?\n\nThis action cannot be undone.')) return;
    try { await apiDel(`/crm/contacts/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error('Failed'); }
  };

  // HubSpot-style: on email blur, resolve the mail domain to a
  // company. If one already exists, auto-link it. If not, offer to auto-create.
  const [domainLookupBusy, setDomainLookupBusy] = useState(false);
  const autoResolveCompanyFromEmail = async () => {
    const email = (form.email || '').trim();
    // Only run for valid-looking emails AND only if the user hasn't already picked
    // a company. Editing flow: never override an existing company link silently.
    if (!email || !email.includes('@') || form.company_id) return;
    setDomainLookupBusy(true);
    try {
      const res = await apiGet(`/crm/companies/by-domain?email=${encodeURIComponent(email)}&enrich=true`);
      if (res?.is_free_email) return; // gmail/yahoo etc. — don't infer a "Gmail Inc" company
      if (res?.match && res?.company?.id) {
        setForm((f) => ({ ...f, company_id: res.company.id }));
        if (!companies.find((c) => c.id === res.company.id)) {
          setCompanies((prev) => [...prev, res.company]);
        }
        toast.success(`Linked to existing company "${res.company.name}"`, { id: 'crm-domain-autolink' });
        return;
      }
      if (res?.suggested_name) {
        const enrichedTag = res.enrichment_source === 'claude-sonnet-4-5'
          ? '\n\n(Resolved via AI lookup — verify before saving.)'
          : res.enrichment_source === 'cache'
          ? '\n\n(Resolved from your team\'s previously-saved lookup.)'
          : '';
        const ok = window.confirm(
          `No company on file for "${res.domain}".\n\n` +
          `Create a new company called "${res.suggested_name}" and link this contact to it?` +
          enrichedTag
        );
        if (!ok) return;
        const newCo = await apiPost('/crm/companies', {
          name: res.suggested_name,
          website: res.suggested_website || `https://${res.domain}`,
          industry: res.suggested_industry || undefined,
          city: res.suggested_city || undefined,
          country: res.suggested_country || 'India',
        });
        setCompanies((prev) => [...prev, newCo]);
        setForm((f) => ({ ...f, company_id: newCo.id }));
        toast.success(`Created company "${newCo.name}" and linked`);
      }
    } catch (err) {
      // Silent — auto-link is a convenience, never block the user from saving
      // because the lookup failed (e.g., network blip on slow networks).
    } finally {
      setDomainLookupBusy(false);
    }
  };

  const openEdit = (c) => {
    setEditing(c); setForm({ ...initialForm, ...c, tags: c.tags || [] }); setShowForm(true);
  };

  const toggleTag = (tag) => {
    const tags = form.tags || [];
    setForm({ ...form, tags: tags.includes(tag) ? tags.filter(t => t !== tag) : [...tags, tag] });
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Business Development</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Contacts</h1>
          <p className="text-sm text-slate-600 mt-1">People at your client companies. Email is mandatory and globally unique.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const token = localStorage.getItem('access_token');
                const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/contacts-import/sample-template`, {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'crm-contacts-template.csv';
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              } catch (e) { toast.error('Could not download template: ' + (e.message || '')); }
            }}
            className="px-3 py-2 text-xs font-display font-bold uppercase tracking-wider bg-white border border-slate-300 text-slate-700 rounded-sm hover:border-[#1F7A6E] hover:text-[#1F7A6E] flex items-center gap-1.5"
            title="Download sample CSV template"
            data-testid="contacts-sample-template-btn"
          >
            <Download size={13} /> Sample
          </button>
          <label
            className="px-3 py-2 text-xs font-display font-bold uppercase tracking-wider bg-white border border-slate-300 text-slate-700 rounded-sm hover:border-[#1F7A6E] hover:text-[#1F7A6E] flex items-center gap-1.5 cursor-pointer"
            data-testid="contacts-bulk-upload-btn"
          >
            <Upload size={13} /> Bulk Upload
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const fd = new FormData();
                fd.append('file', f);
                try {
                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/contacts-import/upload`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || ''}` },
                    body: fd,
                  });
                  const data = await res.json();
                  if (!res.ok) throw new Error(data?.detail || 'Import failed');
                  toast.success(`Imported ${data.created} contact(s)` + (data.skipped ? ` · skipped ${data.skipped}` : ''));
                  if (data.errors?.length) {
                    console.warn('Import warnings', data.errors);
                  }
                  load();
                } catch (err) {
                  toast.error(err?.message || 'Import failed');
                }
                e.target.value = '';
              }}
              className="hidden"
            />
          </label>
          <button onClick={() => { setEditing(null); setForm(initialForm); setShowForm(true); }} className="btn-primary flex items-center gap-2" data-testid="add-contact-btn">
            <Plus size={16} /> Add Contact
          </button>
        </div>
      </div>

      <div className="mb-3 flex items-center justify-between">
        <ScopeToggle value={scope} onChange={setScope} testid="scope-toggle-contacts" />
        <div className="text-xs text-slate-500">
          Showing <b className="text-[#1F7A6E]">{contacts.length}</b> contacts · <span className="uppercase tracking-wider font-display font-bold text-[10px]">{scope}</span>
        </div>
      </div>

      {/* Rich filters + saved segments */}
      <SegmentedFilterBar
        entityType="contacts"
        items={decoratedContacts}
        filterDefs={filterDefs}
        onFiltered={setFilteredContacts}
      />
      <div className="mb-3"></div>

      <BulkActionBar count={selection.count} onClear={selection.clear} className="mb-3">
        <button onClick={handleBulkExport} className="text-xs font-display font-bold uppercase tracking-wider px-3 py-1.5 bg-white/15 text-white rounded-sm hover:bg-white/25 flex items-center gap-1" data-testid="bulk-export-contacts"><Download size={11} /> Export CSV</button>
        <button onClick={handleBulkDelete} className="text-xs font-display font-bold uppercase tracking-wider px-3 py-1.5 bg-red-600 text-white rounded-sm hover:bg-red-700 flex items-center gap-1" data-testid="bulk-delete-contacts"><Trash2 size={11} /> Delete</button>
      </BulkActionBar>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="contacts-table">
        <table className="w-full dense-table">
          <thead><tr>
            <th className="w-10"><input type="checkbox" className="w-4 h-4 accent-[#1F7A6E] cursor-pointer" checked={selection.allSelected(pageIds)} ref={(el) => { if (el) el.indeterminate = selection.someSelected(pageIds); }} onChange={() => selection.toggleAll(pageIds)} data-testid="select-all-contacts" /></th>
            <th {...getProps('name')}>Name <SortIcon dir={sortKey === 'name' ? sortDir : null} /></th>
            <th {...getProps('email')}>Email <SortIcon dir={sortKey === 'email' ? sortDir : null} /></th>
            <th>Phone</th>
            <th {...getProps('designation')}>Designation <SortIcon dir={sortKey === 'designation' ? sortDir : null} /></th>
            <th {...getProps('company_name')}>Company <SortIcon dir={sortKey === 'company_name' ? sortDir : null} /></th>
            <th>Tags</th>
            <th {...getProps('owner')}>Owner <SortIcon dir={sortKey === 'owner' ? sortDir : null} /></th>
            <th {...getProps('created_at')}>Created <SortIcon dir={sortKey === 'created_at' ? sortDir : null} /></th>
            <th></th>
          </tr></thead>
          <tbody>
            {pagination.paged.map(c => (
              <tr key={c.id} onClick={() => setDrawerContact(c)} className={`hover:bg-slate-50 cursor-pointer ${selection.isSelected(c.id) ? 'bg-teal-50/40' : ''}`} data-testid={`contact-row-${c.id}`}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" className="w-4 h-4 accent-[#1F7A6E] cursor-pointer" checked={selection.isSelected(c.id)} onChange={() => selection.toggle(c.id)} data-testid={`select-contact-${c.id}`} />
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-sm bg-emerald-50 text-emerald-700 flex items-center justify-center font-display font-bold text-xs">{(c.name || '?').slice(0,2).toUpperCase()}</div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-display font-semibold text-[#0F172A]">{c.name}</span>
                      {c.is_primary && <Star size={11} className="text-amber-500 fill-amber-500" />}
                    </div>
                  </div>
                </td>
                <td className="text-xs">
                  <a href={`mailto:${c.email}`} className="text-[#1F7A6E] hover:underline inline-flex items-center gap-1"><Mail size={11} /> {c.email}</a>
                </td>
                <td className="text-xs">{c.phone ? <span className="inline-flex items-center gap-1"><Phone size={11} /> {c.phone}</span> : '—'}</td>
                <td className="text-xs">{c.designation || '—'}</td>
                <td className="text-xs">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span>{c.company_name || '—'}</span>
                    {c.is_consultant_contact && (
                      <span
                        className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-sm font-display font-bold"
                        title={`Contact at ${c.consultant_type} consultant firm`}
                        data-testid={`consultant-badge-${c.id}`}
                      >
                        {c.consultant_type}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {(c.tags || []).slice(0, 3).map(t => <span key={t} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-teal-50 text-[#1F7A6E] rounded-sm">{t}</span>)}
                  </div>
                </td>
                <td className="text-xs">{c.owner || '—'}</td>
                <td className="text-xs whitespace-nowrap">{formatDate(c.created_at)}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate('/app/crm/leads', {
                        state: {
                          prefill: {
                            company_id: c.company_id || '',
                            contact_id: c.id,
                            client: c.company_name || '',
                            contact_person: c.name || '',
                            email: c.email || '',
                            phone: c.phone || '',
                          }
                        }
                      })}
                      className="text-emerald-600 hover:text-emerald-800"
                      title="Push to Lead Tracker"
                      data-testid={`push-contact-to-lead-${c.id}`}
                    >
                      <Send size={14} />
                    </button>
                    <button onClick={() => openEdit(c)} className="text-slate-500 hover:text-[#1F7A6E]"><Edit2 size={14} /></button>
                    <button onClick={() => remove(c.id)} className="text-slate-500 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!contacts.length && <tr><td colSpan="10" className="p-8 text-center text-sm text-slate-500">No contacts. Add a contact to get started.</td></tr>}
          </tbody>
        </table>
        {contacts.length > 0 && (
          <div className="border-t border-slate-200 px-4 py-3">
            <Pagination page={pagination.page} pageSize={pagination.pageSize} total={pagination.total} start={pagination.start} end={pagination.end} onPageChange={pagination.setPage} onPageSizeChange={pagination.setPageSize} label="contacts" />
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">{editing ? 'Edit Contact' : 'Add Contact'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-slate-500"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Name *</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" required data-testid="contact-name-input" /></div>
                <div>
                  <label className="label">Email * <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-1">(unique · auto-links company)</span></label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    onBlur={autoResolveCompanyFromEmail}
                    className="input"
                    required
                    data-testid="contact-email-input"
                  />
                  {domainLookupBusy && <div className="text-[10px] text-slate-400 mt-0.5">Looking up company for this domain…</div>}
                </div>
                <div><label className="label">Phone</label><input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" /></div>
                <div><label className="label">Designation</label><input type="text" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="input" /></div>
                <div className="col-span-2">
                  <label className="label">Company</label>
                  <CompanyPicker
                    value={form.company_id}
                    onChange={(id) => setForm({ ...form, company_id: id })}
                  />
                </div>
                <div>
                  <label className="label">Location</label>
                  <LocationCombobox value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="contact-location" />
                </div>
                <div><label className="label">LinkedIn</label><input type="url" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} className="input" placeholder="https://linkedin.com/in/..." /></div>
                <div><label className="label">Contact Owner</label><EmployeePicker value={form.owner} onChange={(v) => setForm({ ...form, owner: v })} testid="contact-owner-picker" restrictToTeam /></div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} />
                Primary contact for this company
              </label>
              <div>
                <label className="label">Tags</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {CRM_TAGS.map(t => (
                    <button key={t} type="button" onClick={() => toggleTag(t)} className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${(form.tags || []).includes(t) ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'border-slate-300 text-slate-600 hover:border-[#1F7A6E]'}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div><label className="label">Notes</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" rows={3} /></div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="contact-submit-btn">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {drawerContact && (
        <EntityActivityDrawer
          entityType="contact"
          entityId={drawerContact.id}
          entityLabel={drawerContact.name}
          subtitle={[drawerContact.designation, drawerContact.company_name].filter(Boolean).join(' · ')}
          metaRows={[
            { label: 'Designation', value: drawerContact.designation },
            { label: 'Company', value: drawerContact.company_name },
            { label: 'Email', value: drawerContact.email },
            { label: 'Phone', value: drawerContact.phone },
            { label: 'LinkedIn', value: drawerContact.linkedin },
            { label: 'Location', value: drawerContact.location },
            { label: 'Owner', value: drawerContact.owner },
          ]}
          onClose={() => setDrawerContact(null)}
          bottomSlot={contactDeals.length > 0 ? (
            <div data-testid="contact-associated-deals">
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-2 flex items-center gap-1.5">
                <Briefcase size={11} /> Associated Deals ({contactDeals.length})
              </div>
              <div className="space-y-1.5">
                {contactDeals.map(d => (
                  <div key={d.id} className="flex items-center justify-between bg-white border border-slate-200 rounded-sm px-3 py-2 text-xs">
                    <div>
                      <div className="font-semibold text-slate-800">{d.name}</div>
                      {d.company_name && <div className="text-slate-500">{d.company_name}</div>}
                    </div>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-slate-100 text-slate-600 rounded-sm">{d.stage || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        />
      )}
    </div>
  );
}
